import { z } from "@msuite/utils/lib/zod";
import { defineQuery } from "@rocicorp/zero";
import { zql } from "../zql";

export const planQueries = {
	// The plan is the tenancy root of the sync path: it is always scoped to the caller's
	// organisation from the server-derived context, never from an argument, and everything
	// else on the board is reachable only through it.
	//
	// Below the root, a hop compares `ctx.orgId` wherever that is the right answer, which on this
	// board is not everywhere. Work, its tickets and their types, vehicles and the day's own
	// vehicles carry the plan's organisation, which here is the caller's, so they compare it.
	// Crews and their staffing do not: a partner crew holds a foreign organisation by design and
	// its people carry the crew's, and the host is entitled to both — filtering them would empty
	// the partner column instead of scoping it. Two hops have nothing to compare: `organization`,
	// whose `id` *is* an organisation and whose partner row is foreign on purpose, and the `user`
	// behind a staffing row, since `users` carries no organisation column at all.
	// `queries/delivered-tables.test.ts` holds that list and turns red when it changes.
	byDate: defineQuery(z.object({ date: z.string() }), ({ args, ctx }) => {
		return zql.plans
			.where("date", "=", args.date)
			.where("orgId", "=", ctx.orgId)
			.related("crews", (crews) =>
				crews
					.orderBy("orgId", "asc")
					.orderBy("ranking", "asc")
					.related("organization", (organization) => organization.orderBy("name", "asc"))
					.related("ticketAssignments", (ticketAssignments) =>
						ticketAssignments
							.where("orgId", "=", ctx.orgId)
							.orderBy("ranking", "asc")
							// The client contact rides along for the board card's client line. A contact
							// row never syncs outside its organisation, so the filter is stated here even
							// though an own-plan project's client already is the caller's own — both plan
							// queries carry the same rule.
							.related("project", (project) =>
								project
									.where("orgId", "=", ctx.orgId)
									.related("_client", (client) => client.where("orgId", "=", ctx.orgId)),
							)
							.related("ticket", (ticket) =>
								ticket
									.where("orgId", "=", ctx.orgId)
									.related("ticketType", (ticketType) => ticketType.where("orgId", "=", ctx.orgId)),
							),
					)
					.related("userAssignments", (userAssignments) =>
						userAssignments.orderBy("ranking", "asc").related("user"),
					)
					// The assignment carries the same organisation filter the standalone
					// vehicleAssignments.byCrewId query applies, so the vehicle it brings along
					// stays within the caller's own fleet.
					.related("vehicleAssignment", (vehicleAssignment) =>
						vehicleAssignment
							.where("orgId", "=", ctx.orgId)
							.related("vehicle", (vehicle) => vehicle.where("orgId", "=", ctx.orgId))
							.one(),
					),
			)
			.related("vehicleAssignmentsPlan", (vehicleAssignmentPlan) =>
				vehicleAssignmentPlan
					.where("orgId", "=", ctx.orgId)
					.related("vehicle", (vehicle) => vehicle.where("orgId", "=", ctx.orgId))
					.orderBy("createdAt", "asc"),
			)
			.one();
	}),

	// The boards of other organisations that carry a crew of the caller's — the one thing a partner
	// sees outside its own tenancy, and it sees nothing else of them. `crews` is filtered to the
	// caller's own, so the host's columns never leave the host, and the query is read-only by
	// construction: no mutator accepts a plan the caller does not own, and every write policy on
	// the rows below compares an organisation before it admits anything.
	//
	// Nothing under that crew compares `ctx.orgId`, and that is the query rather than an omission:
	// the work on a guest column, its ticket and the ticket's type, the project it belongs to and
	// the day's own vehicles all carry the *host's* organisation, and showing them to the crew
	// standing on that column is what the query is for. The crew filter is the boundary, and every
	// row below is reached through it.
	//
	// Reached through the sync path rather than through the static endpoint that bypasses row level
	// security. The endpoint would have had to be filtered back down to avoid showing the caller's
	// own columns twice, and it is the path being removed elsewhere.
	guestByDate: defineQuery(z.object({ date: z.string() }), ({ args, ctx }) => {
		return (
			zql.plans
				.where("date", "=", args.date)
				.where("orgId", "!=", ctx.orgId)
				.whereExists("crews", (crews) => crews.where("orgId", "=", ctx.orgId))
				// The host organisation rides along for the section title — one row the caller could
				// already reach through organizations.byId, nothing more.
				.related("organization")
				.related("crews", (crews) =>
					crews
						.where("orgId", "=", ctx.orgId)
						.orderBy("ranking", "asc")
						.related("organization", (organization) => organization.orderBy("name", "asc"))
						.related("ticketAssignments", (ticketAssignments) =>
							ticketAssignments
								.orderBy("ranking", "asc")
								// The host's project rides along so the crew knows where to work, but its
								// client contact stays home: the contact is pinned to the caller's
								// organisation, which on a guest board is never the project's, so no client
								// row crosses the partnership — the rule the static endpoint states by
								// nulling clientName on foreign projects.
								.related("project", (project) =>
									project.related("_client", (client) => client.where("orgId", "=", ctx.orgId)),
								)
								// The type crosses with the ticket: the board card draws the host's icon and
								// colour from this row, since the viewer's own catalogue never holds it.
								.related("ticket", (ticket) => ticket.related("ticketType")),
						)
						.related("userAssignments", (userAssignments) =>
							userAssignments.orderBy("ranking", "asc").related("user"),
						)
						// A vehicle on a crew belongs to the plan's organisation — the host supplies
						// it — so on a guest board the assignment filters away and no plate syncs,
						// matching what the board shows today.
						.related("vehicleAssignment", (vehicleAssignment) =>
							vehicleAssignment
								.where("orgId", "=", ctx.orgId)
								.related("vehicle", (vehicle) => vehicle.where("orgId", "=", ctx.orgId))
								.one(),
						),
				)
		);
	}),
};
